import React, { useState } from 'react';
import { Button, message } from 'antd';
import YAML from 'yamljs';
import { CodeOutlined, FormOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';
import { ANTD_GRAY } from '../../../entity/shared/constants';
import { YamlEditor } from './YamlEditor';
import RecipeForm from './RecipeForm/RecipeForm';
import { SourceBuilderState, SourceConfig } from './types';
import { LOOKER, LOOK_ML } from './constants';
import { LookerWarning } from './LookerWarning';
import { useTranslation } from '../../../../i18n/useTranslation';

export const ControlsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
`;

const BorderedSection = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
    border: solid ${ANTD_GRAY[4]} 0.5px;
`;

interface Props {
    state: SourceBuilderState;
    isEditing: boolean;
    displayRecipe: string;
    sourceConfigs?: SourceConfig;
    applianceId: string;
    setStagedRecipe: (recipe: string) => void;
    setApplianceId: (id: string) => void;
    setRecipeVersion: (version: string) => void;
    onClickNext: () => void;
    goToPrevious?: () => void;
}

function RecipeBuilder(props: Props) {
    const {
        state,
        isEditing,
        applianceId,
        displayRecipe,
        sourceConfigs,
        setStagedRecipe,
        setApplianceId,
        setRecipeVersion,
        onClickNext,
        goToPrevious,
    } = props;
    const { type } = state;
    const [isViewingForm, setIsViewingForm] = useState(true);
    const { t } = useTranslation();
    function switchViews(isFormView: boolean) {
        try {
            YAML.parse(displayRecipe);
            setIsViewingForm(isFormView);
        } catch (e) {
            const messageText = (e as any).parsedLine
                ? `Fix line ${(e as any).parsedLine} in your recipe`
                : t('Please fix your recipe');
            message.warn(`Found invalid YAML. ${messageText} in order to switch views.`);
        }
    }

    return (
        <div>
            {(type === LOOKER || type === LOOK_ML) && <LookerWarning type={type} />}
            <div className="f-btn-tabs f-btn-tabs-full-width mb-4">
                <Button
                    type="text"
                    aria-label={t('Form')}
                    onClick={() => switchViews(true)}
                    data-testid="recipe-builder-form-button"
                    className={isViewingForm ? 'active' : ''}
                >
                    <FormOutlined /> {t('Form')}
                </Button>
                <Button
                    type="text"
                    aria-label={t('YAML')}
                    onClick={() => switchViews(false)}
                    data-testid="recipe-builder-yaml-button"
                    className={!isViewingForm ? 'active' : ''}
                >
                    <CodeOutlined /> {t('YAML')}
                </Button>
            </div>
            {isViewingForm && (
                <RecipeForm
                    state={state}
                    isEditing={isEditing}
                    applianceId={applianceId}
                    displayRecipe={displayRecipe}
                    sourceConfigs={sourceConfigs}
                    setStagedRecipe={setStagedRecipe}
                    setApplianceId={setApplianceId}
                    setRecipeVersion={setRecipeVersion}
                    onClickNext={onClickNext}
                    goToPrevious={goToPrevious}
                />
            )}
            {!isViewingForm && (
                <>
                    <BorderedSection>
                        <YamlEditor initialText={displayRecipe} onChange={setStagedRecipe} />
                    </BorderedSection>
                    <ControlsContainer>
                        <Button disabled={isEditing} onClick={goToPrevious}>
                            {t('Previous')}
                        </Button>
                        <Button data-testid="recipe-builder-next-button" onClick={onClickNext}>
                            {t('Next')}
                        </Button>
                    </ControlsContainer>
                </>
            )}
        </div>
    );
}

export default RecipeBuilder;
